<script setup lang="ts">
import { Form, router } from "@inertiajs/vue3"
import { ref, watch } from "vue"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Textarea } from "@/components/ui/textarea"
import { dateFormatter } from "@/lib/formatters"
import { cn, formatPrice } from "@/lib/utils"
import { approveManagerStudyPath, rejectManagerStudyPath } from "@/routes"
import type { ManagerStudy } from "@/types/serializers"

const props = defineProps<{
  data: ManagerStudy
}>()

const open = ref(false)
const showRejectForm = ref(false)
const rejectReason = ref("")

function closeDialog() {
  showRejectForm.value = false
  rejectReason.value = ""
  open.value = false
}

function approve() {
  router.visit(approveManagerStudyPath(props.data.id), {
    method: "post",
    onSuccess: () => closeDialog(),
  })
}

function openRejectForm() {
  showRejectForm.value = true
}

function cancelReject() {
  showRejectForm.value = false
  rejectReason.value = ""
}

watch(open, (value) => {
  if (!value) cancelReject()
})
</script>

<template>
  <Dialog v-model:open="open">
    <DialogTrigger>
      <Button variant="link" class="h-4 px-0">Abrir</Button>
    </DialogTrigger>
    <DialogContent :class="cn('sm:max-w-lg')">
      <DialogHeader>
        <DialogTitle :class="cn('font-bold')">
          Solicitud ID: {{ data.id }}
        </DialogTitle>
        <DialogDescription>Revisión de detalles del cursoç</DialogDescription>
      </DialogHeader>
      <div class="flex flex-col gap-2">
        <Card>
          <CardContent>
            <p class="mb-4 font-bold">Información del Empleado</p>

            <div class="grid grid-cols-2 gap-2">
              <div class="flex flex-col gap-0.5">
                <p class="text-muted-foreground text-xs">Empleado:</p>
                <p>{{ data.user.full_name }}</p>
              </div>

              <div class="flex flex-col items-end gap-0.5">
                <p class="text-muted-foreground text-xs">Departamento:</p>
                <p>{{ data.user.department.toUpperCase() }}</p>
              </div>

              <div class="flex flex-col gap-0.5">
                <p class="text-muted-foreground text-xs">Comercia ID:</p>
                <p>{{ data.user.ref }}</p>
              </div>

              <div class="flex flex-col items-end gap-0.5">
                <p class="text-muted-foreground text-xs">Título profesional:</p>
                <p>{{ data.user.job_title }}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <p class="mb-4 font-bold">Detalles de la Compra</p>

            <div class="grid grid-cols-2 gap-2">
              <div class="flex flex-col gap-0.5">
                <p class="text-muted-foreground text-xs">
                  Nombre de la formación
                </p>
                <p>{{ data.course_name }}, {{ data.provider }}</p>
              </div>

              <div class="flex flex-col items-end gap-0.5">
                <p class="text-muted-foreground text-xs">Importe:</p>
                <p>{{ formatPrice(data.total_amount) }}</p>
              </div>

              <div class="flex flex-col gap-0.5">
                <p class="text-muted-foreground text-xs">Fechas:</p>
                <p>
                  {{ dateFormatter(data.start_date, {}, "date") }} -
                  {{ dateFormatter(data.end_date, {}, "date") }}
                </p>
              </div>

              <div class="flex flex-col items-end gap-0.5">
                <p class="text-muted-foreground text-xs">Solicitud:</p>
                <p>{{ dateFormatter(data.created_at, {}, "dateTime") }}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <p class="mb-4 font-bold">Documentos adjuntos</p>

            <p>
              <a
                :href="data.syllabus"
                target="_blank"
                download
                class="text-primary underline"
              >
                Programa de contenidos
              </a>
            </p>
            <p>
              <a
                :href="data.payment_receipt"
                target="_blank"
                download
                class="text-primary underline"
              >
                Comprobante de pago
              </a>
            </p>
            <p>
              <a
                :href="data.manager_approval"
                target="_blank"
                download
                class="text-primary underline"
              >
                Screenshot del email de aprobación del manager
              </a>
            </p>
            <p v-if="data.completion_certificate">
              <a
                :href="data.completion_certificate"
                target="_blank"
                download
                class="text-primary underline"
              >
                Certificado de Superación de Formación 2025
              </a>
            </p>
          </CardContent>
        </Card>
      </div>
      <DialogFooter v-if="data.status === 'pending'">
        <Form
          v-if="showRejectForm"
          :action="rejectManagerStudyPath(data.id)"
          method="post"
          class="w-full space-y-3"
          disable-while-processing
          :options="{ preserveScroll: true, preserveState: true }"
          @success="closeDialog"
          #default="{ processing, errors }"
        >
          <div class="space-y-1">
            <p class="text-sm font-medium">Motivo de rechazo</p>
            <Textarea
              v-model="rejectReason"
              name="reject_reason"
              rows="3"
              placeholder="Escribe el motivo del rechazo"
              :disabled="processing"
            />
            <p v-if="errors.reject_reason" class="text-destructive text-sm">
              {{ errors.reject_reason }}
            </p>
          </div>
          <div class="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              :disabled="processing"
              @click="cancelReject"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="destructive"
              :disabled="processing || !rejectReason.trim()"
            >
              Rechazar
            </Button>
          </div>
        </Form>
        <template v-else>
          <Button variant="destructive" size="lg" @click="openRejectForm">
            Rechazar
          </Button>
          <Button size="lg" @click="approve">Aprobar</Button>
        </template>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
